/* PunchoutLaunchOverlay — fullscreen progress curtain shown while we
   contact the sub-supplier's cXML endpoint and 302/window-redirect the
   buyer's browser to the partner's start_url.

   Without this, the buyer sees an unresponsive tile + blank tab for
   the 1–10 seconds it takes for the real Lyreco/Avantor/Grainger
   handshake to complete — leading to repeated clicks and confusion.
*/
import React from "react";
import { Loader2, Network, ShieldCheck } from "lucide-react";

export default function PunchoutLaunchOverlay({ supplier, message }) {
  if (!supplier) return null;
  const logo = supplier.logo_path
    ? `${process.env.REACT_APP_BACKEND_URL}${supplier.logo_path}`
    : supplier.logo_url || null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center"
      data-testid="punchout-launch-overlay"
      role="status"
      aria-live="polite"
    >
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-[min(440px,92vw)] p-8 text-center">
        {/* Logos pair: UniBuy™ → supplier with animated bridge */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#0072CE] text-white font-bold flex items-center justify-center text-xs shadow-md">
            UniBuy™
          </div>
          <div className="flex-1 max-w-[80px] flex items-center">
            <div className="h-0.5 bg-gradient-to-r from-[#0072CE] via-violet-400 to-slate-300 flex-1 relative overflow-hidden">
              <span className="absolute inset-0 bg-white/60 animate-[shimmer_1.2s_linear_infinite] w-1/3" />
            </div>
          </div>
          {logo ? (
            <img
              src={logo}
              alt={supplier.name}
              className="w-12 h-12 rounded-xl bg-white border border-slate-200 object-contain p-1 shadow-md"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 text-slate-700 font-bold flex items-center justify-center text-xs">
              {(supplier.code || "??").slice(0, 2)}
            </div>
          )}
        </div>

        <div className="inline-flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
          <Loader2 size={12} className="animate-spin text-[#0072CE]" />
          {message || "Redirecting in progress"}
        </div>
        <h3 className="font-display font-bold text-slate-900 text-lg mt-2" data-testid="punchout-launch-supplier-name">
          Punching out to {supplier.name}
        </h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Negotiating the secure cXML PunchOut handshake with the supplier&apos;s catalog. This usually takes 2–5 seconds.
        </p>

        <ul className="mt-5 text-left text-xs text-slate-600 space-y-1.5 max-w-[300px] mx-auto">
          <li className="flex items-center gap-2">
            <Network size={11} className="text-[#0072CE]" />
            Sending PunchOutSetupRequest with your buyer credentials
          </li>
          <li className="flex items-center gap-2">
            <ShieldCheck size={11} className="text-emerald-600" />
            Validating shared-secret authentication
          </li>
          <li className="flex items-center gap-2">
            <Loader2 size={11} className="animate-spin text-[#0072CE]" />
            Resolving partner start URL …
          </li>
        </ul>

        <p className="mt-5 text-[10px] text-slate-400 uppercase tracking-wider">
          Do not close this window.
        </p>
      </div>

      {/* Inline keyframes — keeps the overlay self-contained. */}
      <style>{`
        @keyframes shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
      `}</style>
    </div>
  );
}
